/**
 * Pricing shown on the subscription paywall, derived entirely from the backend
 * catalogue. The cards advertise a *per-month* rate while the backend prices a
 * whole period ("3 Month" costs 17100), so every figure on a card — monthly
 * rate, period total, savings, discount badge — is computed here from the
 * plan's `amount` and the length of its period.
 */

export type CardKey = "1m" | "3m" | "12m";

export type CardPricing = {
  /** Advertised headline rate: period total divided by its months. */
  perMonth: number;
  /** What the user is actually charged for the whole period. */
  total: number;
  /** Saved against paying the shortest plan's rate every month. */
  savings: number;
  /** Rounded percentage off the monthly rate; 0 when there is nothing to show. */
  discountPct: number;
  /** Savings expressed in free months — the "Get 1.8 Months Free" badge. */
  freeMonths: number;
};

const MONTHS: Record<CardKey, number> = { "1m": 1, "3m": 3, "12m": 12 };

export function monthsForCard(key: CardKey): number {
  return MONTHS[key];
}

export function cardPricing(
  total: number,
  months: number,
  baseMonthly: number | null,
): CardPricing {
  const perMonth = months > 0 ? total / months : total;
  // A plan priced above the baseline is not a discount — clamp instead of
  // rendering a negative saving.
  const savings =
    baseMonthly && months > 0 ? Math.max(0, baseMonthly * months - total) : 0;
  const discountPct =
    baseMonthly && savings > 0 ? Math.round((1 - perMonth / baseMonthly) * 100) : 0;
  const freeMonths = baseMonthly ? savings / baseMonthly : 0;
  return { perMonth, total, savings, discountPct, freeMonths };
}

/**
 * Money as the paywall design renders it: grouped thousands, no decimals
 * ("$6,000"). Deliberately not `formatMoney` from money.ts — that one is tuned
 * for the compact figures in Settings and prints "$ 6000".
 */
export function formatPaywallMoney(
  amount: number,
  currency: string,
  lang: "en" | "ru",
): string {
  const locale = lang === "ru" ? "ru-RU" : "en-US";
  const value = Number.isFinite(amount) ? amount : 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Unknown/blank currency code — Intl throws rather than degrading.
    return `${Math.round(value)} ${currency}`.trim();
  }
}
